import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Pencil, CheckCircle2, Circle, PlayCircle, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../lib/format";

const STAGE_TONE = {
  pending: "bg-white text-[#8C9096] border-[#E5E0D8]",
  in_progress: "bg-sky-50 text-sky-700 border-sky-200",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  skipped: "bg-[#F0EBE0] text-[#8C9096] border-[#E5E0D8] line-through",
};
const STAGE_ICON = { pending: Circle, in_progress: PlayCircle, done: CheckCircle2, skipped: SkipForward };

export default function BookingTimelineStrip({ bookingId, canEdit = false }) {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/bookings/${bookingId}/timeline`).then(({ data }) => { setStages(data.stages || []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { if (bookingId) load(); }, [bookingId]);

  const openEdit = () => { setDraft(stages.map((s) => ({ ...s }))); setEditOpen(true); };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/bookings/${bookingId}/timeline`, { stages: draft });
      toast.success("Timeline diperbarui");
      setEditOpen(false); load();
    } catch (e) { if (!e?.__preview_blocked) toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  if (loading) return null;
  if (stages.length === 0) return null;

  return (
    <>
      <Card className="border border-[#E5E0D8] bg-white shadow-none p-3" data-testid="booking-timeline-strip">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-[#8C9096] uppercase tracking-wider">Timeline Acara</div>
          {canEdit && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={openEdit} data-testid="timeline-edit-btn">
              <Pencil className="w-3 h-3 mr-1"/>Edit
            </Button>
          )}
        </div>
        <div className="flex items-start gap-1 overflow-x-auto pb-1">
          {stages.map((s, i) => {
            const Icon = STAGE_ICON[s.status] || Circle;
            return (
              <div key={s.key} className="flex items-start gap-1 shrink-0">
                <div className="flex flex-col items-center min-w-[92px]">
                  <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center ${STAGE_TONE[s.status] || STAGE_TONE.pending}`} title={s.note}>
                    <Icon className="w-4 h-4"/>
                  </div>
                  <div className="mt-1 text-[11px] font-semibold text-center">{s.label}</div>
                  <div className="text-[10px] text-[#8C9096]">{s.date ? formatDate(s.date) : "-"}</div>
                </div>
                {i < stages.length - 1 && <div className="w-6 h-0.5 bg-[#E5E0D8] mt-4"/>}
              </div>
            );
          })}
        </div>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl" data-testid="timeline-edit-modal">
          <DialogHeader><DialogTitle>Edit Timeline Booking</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {draft.map((s, i) => (
              <div key={s.key} className="grid grid-cols-12 gap-2 items-center border border-[#E5E0D8] rounded-md p-2">
                <div className="col-span-4 font-medium text-sm">{s.label}</div>
                <Input className="col-span-4 h-9" type="date" value={(s.date || "").slice(0,10)} onChange={(e) => setDraft((d) => d.map((x, xi) => xi === i ? { ...x, date: e.target.value } : x))} data-testid={`ts-date-${s.key}`} />
                <div className="col-span-4">
                  <Select value={s.status} onValueChange={(v) => setDraft((d) => d.map((x, xi) => xi === i ? { ...x, status: v } : x))}>
                    <SelectTrigger className="h-9" data-testid={`ts-status-${s.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">Sedang Berjalan</SelectItem>
                      <SelectItem value="done">Selesai</SelectItem>
                      <SelectItem value="skipped">Dilewati</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="ts-save" className="bg-[#B7A175] hover:bg-[#A08A5D] text-white">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
